import type { ContentField } from "@/types/template";
import type { SubTemplateData } from "@/types/evaluation";
import { TextInput } from "@/components/filler/fields/TextInput";
import { SelectInput } from "@/components/filler/fields/SelectInput";
import { CheckboxListInput } from "@/components/filler/fields/CheckboxListInput";
import { StatementListInput } from "@/components/filler/fields/StatementListInput";
import { ScoreInput } from "@/components/filler/fields/ScoreInput";
import { GridInput } from "@/components/filler/fields/GridInput";
import { SubTemplateRenderer } from "@/components/filler/fields/SubTemplateRenderer";
import { DateInput } from "@/components/filler/fields/DateInput";
import { isFieldComplete } from "@/utils/completion";
import type {
  TextField,
  SelectField,
  CheckboxListField,
  StatementListField,
  ScoreField,
  GridField,
  SubTemplateField,
  DateField,
} from "@/types/template";

interface Props {
  field: ContentField;
  value: unknown;
  subTemplateData?: SubTemplateData;
  onChange: (value: unknown) => void;
  onSubTemplateChange: (data: SubTemplateData) => void;
  interpolate: (text: string) => string;
  touched?: boolean;
  onBlur?: () => void;
  compact?: boolean;
}

function FieldLabel({ field }: Readonly<{ field: TextField | DateField }>) {
  return (
    <label>
      {field.label}
      {!field.optional && field.label && <span className="field-required-marker"> *</span>}
      {field.optional && field.label && <span className="field-optional-label"> (optional)</span>}
    </label>
  );
}

function renderTextField(
  field: TextField,
  value: unknown,
  onChange: (v: unknown) => void,
  interpolate: (t: string) => string,
  onBlur: (() => void) | undefined,
  showError: boolean,
) {
  return (
    <div className={`filler-field ${showError ? "field-error" : ""}`} data-field-id={field.id}>
      <FieldLabel field={field} />
      <TextInput
        field={field}
        value={(typeof value === "string" ? value : null) ?? field.defaultValue}
        onChange={onChange}
        interpolate={interpolate}
        onBlur={onBlur}
      />
    </div>
  );
}

function renderSelectField(
  field: SelectField,
  value: unknown,
  onChange: (v: unknown) => void,
  compact: boolean | undefined,
  showError: boolean,
) {
  return (
    <div className={`filler-field ${showError ? "field-error" : ""}`} data-field-id={field.id}>
      <label>
        {field.label}
        <span className="field-required-marker"> *</span>
      </label>
      <SelectInput
        field={field}
        value={(value as string | undefined) ?? ""}
        onChange={onChange}
        compact={compact}
      />
    </div>
  );
}

function renderCheckboxListField(
  field: CheckboxListField,
  value: unknown,
  onChange: (v: unknown) => void,
) {
  return (
    <div className="filler-field" data-field-id={field.id}>
      <label>{field.label}</label>
      <CheckboxListInput
        field={field}
        value={(value as string[] | undefined) ?? (field.defaultAll ? [...field.items] : [])}
        onChange={onChange}
      />
    </div>
  );
}

function renderStatementListField(
  field: StatementListField,
  value: unknown,
  onChange: (v: unknown) => void,
  showError: boolean,
) {
  return (
    <div className={`filler-field ${showError ? "field-error" : ""}`} data-field-id={field.id}>
      <label>
        {field.label}
        <span className="field-required-marker"> *</span>
      </label>
      <StatementListInput
        field={field}
        value={
          (value as { text: string; checked: boolean }[] | undefined) ??
          field.items.map((text) => ({ checked: true, text }))
        }
        onChange={onChange}
      />
    </div>
  );
}

function renderScoreField(
  field: ScoreField,
  value: unknown,
  onChange: (v: unknown) => void,
  compact: boolean | undefined,
  showError: boolean,
) {
  return (
    <div className={`filler-field ${showError ? "field-error" : ""}`} data-field-id={field.id}>
      <ScoreInput
        field={field}
        value={value as number | undefined}
        onChange={onChange}
        compact={compact}
      />
    </div>
  );
}

function renderGridField(
  field: GridField,
  value: unknown,
  onChange: (v: unknown) => void,
  showError: boolean,
) {
  return (
    <div className={`filler-field ${showError ? "field-error" : ""}`} data-field-id={field.id}>
      <label>
        {field.label}
        <span className="field-required-marker"> *</span>
      </label>
      <GridInput
        field={field}
        value={(value as Record<string, Record<string, string>> | undefined) ?? {}}
        onChange={onChange}
      />
    </div>
  );
}

function renderSubTemplateField(
  field: SubTemplateField,
  subTemplateData: SubTemplateData | undefined,
  onSubTemplateChange: (data: SubTemplateData) => void,
) {
  return (
    <div className="filler-field" data-field-id={field.id}>
      <SubTemplateRenderer field={field} data={subTemplateData} onChange={onSubTemplateChange} />
    </div>
  );
}

function renderDateField(
  field: DateField,
  value: unknown,
  onChange: (v: unknown) => void,
  onBlur: (() => void) | undefined,
  showError: boolean,
) {
  return (
    <div className={`filler-field ${showError ? "field-error" : ""}`} data-field-id={field.id}>
      <FieldLabel field={field} />
      <DateInput
        field={field}
        value={(typeof value === "string" ? value : null) ?? field.defaultValue}
        onChange={onChange}
        onBlur={onBlur}
      />
    </div>
  );
}

export function FieldRenderer({
  compact,
  field,
  interpolate,
  onBlur,
  onChange,
  onSubTemplateChange,
  subTemplateData,
  touched,
  value,
}: Readonly<Props>) {
  const showError = touched === true && !isFieldComplete(field, value, subTemplateData);

  switch (field.type) {
    case "text": {
      return renderTextField(field, value, onChange, interpolate, onBlur, showError);
    }
    case "select": {
      return renderSelectField(field, value, onChange, compact, showError);
    }
    case "checkbox-list": {
      return renderCheckboxListField(field, value, onChange);
    }
    case "statement-list": {
      return renderStatementListField(field, value, onChange, showError);
    }
    case "score": {
      return renderScoreField(field, value, onChange, compact, showError);
    }
    case "grid": {
      return renderGridField(field, value, onChange, showError);
    }
    case "sub-template": {
      return renderSubTemplateField(field, subTemplateData, onSubTemplateChange);
    }
    case "date": {
      return renderDateField(field, value, onChange, onBlur, showError);
    }
    default: {
      return null;
    }
  }
}
